// Черновата на листа „Доставка" — общото `useWorksheetDraft` със снимката на
// доставката (формат v4, `DeliveryDraft`): флагът „незапазени промени",
// autosave-ът (браузър + сървър) при нова доставка, черновата при отваряне
// (без prefill и без фактура над стокови — `opensFromDraft`) и нещата за
// въпросите и футъра (редовете, „Изтрий черновата" · „Запази временно").
// Листът подава състоянието си лениво (`sheet`) — редовете идват от hook-а
// по-долу в тялото.
import { useAppSelector } from "../../../../store/hooks";
import type { DeliveryDocumentType } from "../../../../api/inventory";
import { summaryLineItem } from "../../components/confirm/confirmPreviews";
import type { DraftFooterProps } from "../../components/drafts/DraftWorksheetParts";
import { useWorksheetDraft } from "../../components/drafts/useWorksheetDraft";
import {
  DELIVERY_DRAFT_STORE,
  hasDeliveryDraftContent,
  isRowTouched,
  type DeliveryDraft,
  type DeliveryRowDraft,
} from "./deliveryWorksheetShared";
import { opensFromDraft, type DeliveryRouterState } from "./useDeliveryLoad";
import type { DeliveryRowsSummary } from "./useDeliveryLeave";

// Състоянието на листа в момента на снимката
type SheetState = {
  date: string;
  invoiceNumber: string;
  supplierId: number | null;
  supplierName: string | null;
  whId: number | null;
  note: string;
  rows: Record<number, DeliveryRowDraft>;
  order: number[];
  documentType: DeliveryDocumentType;
  totalGross: number;
};

type Params = {
  mode: "new" | "edit";
  homePath: string;
  routerState: DeliveryRouterState;
  sheet: () => SheetState;
  // „Продукт — количество" на докоснатите редове на чернова (до 5)
  describeLines: (draft: DeliveryDraft | null) => string[];
  // зареди черновата в листа (без `touch`)
  apply: (payload: DeliveryDraft) => void;
  onLoadError: () => void;
};

export const useDeliveryDraft = ({ mode, homePath, routerState, sheet, describeLines, apply, onLoadError }: Params) => {
  const authNames = useAppSelector((s) => s.auth.names);
  const snapshotDraft = (): DeliveryDraft => {
    const state = sheet();
    return {
      v: 4,
      savedAt: new Date().toISOString(),
      savedBy: authNames || null,
      date: state.date,
      invoiceNumber: state.invoiceNumber,
      supplierId: state.supplierId,
      whId: state.whId,
      note: state.note,
      rows: state.rows,
      order: state.order,
      documentType: state.documentType,
    };
  };
  const touchedCount = () => Object.values(sheet().rows).filter(isRowTouched).length;

  const worksheetDraft = useWorksheetDraft<DeliveryDraft>({
    type: "delivery",
    mode,
    homePath,
    routerState,
    local: DELIVERY_DRAFT_STORE,
    snapshot: () => {
      const state = sheet();
      return {
        payload: snapshotDraft(),
        summary: {
          label: state.supplierName,
          documentType: state.documentType,
          linesCount: touchedCount(),
          total: state.totalGross,
        },
      };
    },
    resume: { enabled: opensFromDraft(mode, routerState), hasContent: hasDeliveryDraftContent, apply, onLoadError },
  });

  // Редовете за въпросите „Излизане без запис" и „Изтриване на черновата"
  const describeRows = (): DeliveryRowsSummary => ({
    lines: describeLines(snapshotDraft()),
    total: touchedCount(),
  });

  // „Изтрий черновата" · „Запази временно" за футъра — вика се в рендера на
  // футъра, когато редовете вече са сметнати
  const footer = (): DraftFooterProps => ({
    canDeleteDraft: mode === "new" && (worksheetDraft.sync.draftId != null || touchedCount() > 0),
    describeDraft: () => {
      const summary = describeRows();
      return { label: sheet().supplierName, items: summary.lines.map(summaryLineItem), total: summary.total };
    },
    onDeleteDraft: worksheetDraft.deleteDraft,
    onSaveTemp: worksheetDraft.saveTemp,
  });

  return { ...worksheetDraft, describeRows, footer };
};
